import { addWeeks } from 'lib/datetime/dateUtils'
import {
  get as getLocalStorageItem,
  isPersistent as isLocalStoragePersistent,
  set as setLocalStorageItem,
} from 'lib/storage/isomorphicLocalStorage'
import { get as getVersioned, set as setVersioned } from 'lib/storage/versionedLocalStorageUtils'
import { isExpiringDismissalActive, setExpiringDismissal } from './expiringDismissalEntry'

const SNOOZE_KEY = 'tripChatVoicePromptsSnooze'
const DISMISSALS_KEY = 'tripChatVoicePromptDismissals'
const DISMISSALS_VERSION = 1
const VOICE_INPUT_USED_KEY = 'tripChatVoiceInputUsed'
const CHANGE_EVENT = 'tripChatVoicePromptsChange'

const SNOOZE_WEEKS = 2
const MAX_DISMISSALS = 2

interface DismissalsState {
  count: number
}

function getDismissalCount(): number {
  return getVersioned<DismissalsState>(DISMISSALS_KEY, DISMISSALS_VERSION)?.count ?? 0
}

function snoozeVoicePrompts(now: Date) {
  setExpiringDismissal(SNOOZE_KEY, addWeeks(now, SNOOZE_WEEKS).toISOString())
}

function notifyChange() {
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

/** Fires for writes in this tab and, through the `storage` event, in other tabs. */
export function subscribeToTripChatVoicePrompts(onChange: () => void) {
  window.addEventListener(CHANGE_EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

export function isTripChatVoicePromptSuppressed(): boolean {
  // A dismissal that can't be persisted would be forgotten on the next page load.
  return !isLocalStoragePersistent() ||
    getDismissalCount() >= MAX_DISMISSALS ||
    isExpiringDismissalActive(SNOOZE_KEY)
}

export function recordTripChatVoicePromptDismissed(now = new Date()) {
  try {
    setVersioned<DismissalsState>(DISMISSALS_KEY, DISMISSALS_VERSION, { count: getDismissalCount() + 1 })
  } catch {
    // Quota exceeded; best effort.
  }
  snoozeVoicePrompts(now)
  notifyChange()
}

export function hasUsedTripChatVoiceInput(): boolean {
  return getLocalStorageItem(VOICE_INPUT_USED_KEY) === true
}

export function markTripChatVoiceInputUsed(now = new Date()) {
  try {
    setLocalStorageItem(VOICE_INPUT_USED_KEY, true)
  } catch {
    // Quota exceeded; best effort.
  }
  snoozeVoicePrompts(now)
  notifyChange()
}
